import { useState } from 'react';
import { NavLink, useParams } from 'react-router';
import { BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { manifest, type DocsRole } from './docs-manifest';

interface Props {
  onNavigate?: () => void;
}

export function DocsSidebar({ onNavigate }: Props) {
  const { role: activeRole } = useParams();
  const initial: Record<string, boolean> = {};
  for (const r of manifest.roles) {
    initial[r.slug] = activeRole ? r.slug === activeRole : true;
  }
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initial);

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2 px-4 py-6">
        <BookOpen className="h-6 w-6 text-primary" />
        <NavLink to="/docs" onClick={onNavigate} className="text-lg font-semibold">
          Documentation
        </NavLink>
      </div>

      <nav className="flex-1 space-y-4 px-2 overflow-y-auto">
        {manifest.roles.map((role: DocsRole) => (
          <div key={role.slug}>
            <button
              type="button"
              onClick={() => setExpanded((s) => ({ ...s, [role.slug]: !s[role.slug] }))}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:bg-accent"
            >
              <span>{role.label}</span>
              {expanded[role.slug] ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
            {expanded[role.slug] && (
              <div className="mt-1 space-y-1">
                {role.pages.map((page) => (
                  <NavLink
                    key={page.path}
                    to={`/docs/${page.path}`}
                    end
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-1.5 text-sm transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )
                    }
                  >
                    {page.title}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="border-t px-4 py-3 text-xs text-muted-foreground">
        Docs v{manifest.version}
      </div>
    </div>
  );
}
